import { useEffect, useState } from 'react';
import { api } from '../../client/api';
import { ContentSkeleton } from '../../components/skeleton/ContentSkeleton';
import type { OrgUsageRow, UsageDimension } from '../../client/api';
import { cn } from '../../utils/cn';
import {
  BoPage,
  Section,
  ErrorBanner,
  btnGhost,
  errMsg,
  formatBytes,
  formatBytesLimit,
  formatCountLimit,
} from './ui';

// =============================================================================
// BO · Usage (NBL_REQ_805 / HNBL-111) — per-org usage vs plan limits with
// over-quota highlighting. Read-only aggregate over /api/bo/usage.
// =============================================================================

/** Colour the usage bar: red when over, amber ≥80%, accent otherwise. */
function barColor(d: UsageDimension): string {
  if (d.over) return 'bg-warning';
  if (d.ratio !== null && d.ratio >= 0.8) return 'bg-[#f5a623]';
  return 'bg-accent';
}

function UsageCell({
  d,
  format,
}: {
  d: UsageDimension;
  format: (n: number) => string;
}): JSX.Element {
  const pct = d.ratio === null ? 0 : Math.min(100, Math.round(d.ratio * 100));
  return (
    <div className="flex min-w-[144px] flex-col gap-1">
      <div className={cn('text-[14px]', d.over ? 'font-semibold text-warning' : 'text-text-dim')}>
        {format(d.used)} <span className="text-text-faint">/ {d.limit === null ? '∞' : format(d.limit)}</span>
        {d.over && <span className="ml-1">초과</span>}
      </div>
      {d.limit !== null && (
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-panel-3">
          <div className={cn('h-full transition-all', barColor(d))} style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

export function Usage(): JSX.Element {
  const [rows, setRows] = useState<OrgUsageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      setRows(await api.boListUsage());
      setLoaded(true);
      setErr(null);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  const overCount = rows.filter((r) => r.storage.over || r.devices.over || r.users.over).length;

  return (
    <BoPage
      title="사용량 대시보드"
      subtitle="운영사 전용 — 조직별 사용량과 플랜 한도 비교. 초과 항목은 강조 표시됩니다."
      actions={
        <button type="button" className={btnGhost} onClick={() => void load()} disabled={loading}>
          새로고침
        </button>
      }
    >
      <ErrorBanner message={err} />
      {overCount > 0 && (
        <p className="rounded bg-warning/10 px-3 py-2 text-[14px] text-warning">
          {overCount}개 조직이 한도를 초과했습니다.
        </p>
      )}
      <Section title="조직별 사용량">
        {loading && !loaded && !err ? (
          <ContentSkeleton label="조직별 사용량을 불러오는 중…" />
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-[14px] text-text-faint">조직이 없습니다.</p>
        ) : (
          <div className="overflow-x-auto rounded border border-line">
            <table className="w-full text-[14px]">
              <thead>
                <tr className="border-b border-line bg-panel-2">
                  <th className="px-3 py-2 text-left font-semibold text-text-dim">조직</th>
                  <th className="px-3 py-2 text-left font-semibold text-text-dim">플랜</th>
                  <th className="px-3 py-2 text-left font-semibold text-text-dim">스토리지</th>
                  <th className="px-3 py-2 text-left font-semibold text-text-dim">디바이스</th>
                  <th className="px-3 py-2 text-left font-semibold text-text-dim">사용자</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.orgId} className="border-b border-line last:border-0 hover:bg-panel-hover transition-colors">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5 text-text">
                        {r.orgName}
                        {r.orgStatus === 'suspended' && (
                          <span className="rounded bg-warning/10 px-1.5 py-0.5 text-[12px] text-warning">정지</span>
                        )}
                      </div>
                      <div className="font-mono text-[12px] text-text-faint">{r.orgId}</div>
                    </td>
                    <td className="px-3 py-2 text-text-dim">{r.planName ?? <span className="text-text-faint">미할당</span>}</td>
                    <td className="px-3 py-2"><UsageCell d={r.storage} format={formatBytes} /></td>
                    <td className="px-3 py-2"><UsageCell d={r.devices} format={(n) => String(n)} /></td>
                    <td className="px-3 py-2"><UsageCell d={r.users} format={(n) => String(n)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
      <p className="text-[13px] text-text-faint">
        한도 표기: 스토리지 {formatBytesLimit(null)} · 카운트 {formatCountLimit(null)} = 무제한.
      </p>
    </BoPage>
  );
}
